import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import { Modal, Button, FormInput } from "../../../components/ui";
import * as billingService from "../services/billingService";

/**
 * DigitalPaymentModal - Add/Edit UPI details.
 */
export const DigitalPaymentModal = ({ isOpen, onClose, onSuccess, initialData }) => {
    const [formData, setFormData] = useState({
        providerName: "",
        upiId: "",
        displayName: "",
        isPrimary: false
    });

    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (initialData) {
            setFormData({
                providerName: initialData.providerName || "",
                upiId: initialData.upiId || "",
                displayName: initialData.displayName || "",
                isPrimary: initialData.isPrimary || false
            });
        } else {
            setFormData({
                providerName: "",
                upiId: "",
                displayName: "",
                isPrimary: false
            });
        }
    }, [initialData, isOpen]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({ 
            ...prev, 
            [name]: type === 'checkbox' ? checked : value 
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setIsSubmitting(true);
            if (initialData?._id) {
                await billingService.updateDigitalPayment(initialData._id, formData);
                toast.success("Payment method updated!");
            } else {
                await billingService.createDigitalPayment(formData);
                toast.success("Payment method added!");
            }
            onSuccess();
            onClose();
        } catch (err) {
            toast.error(err.response?.data?.message || "Something went wrong");
        } finally {
            setIsSubmitting(false);
        }
    };

    const footer = (
        <div className="flex gap-3 w-full">
            <Button variant="secondary" onClick={onClose} fullWidth className="rounded-lg font-bold">
                Cancel
            </Button>
            <Button 
                type="button" 
                variant="orange" 
                loading={isSubmitting} 
                onClick={handleSubmit}
                fullWidth 
                className="rounded-lg font-bold shadow-lg shadow-orange-100/50"
            >
                Save Payment Details
            </Button>
        </div>
    );

    return (
        <Modal 
            isOpen={isOpen} 
            onClose={onClose} 
            title={initialData ? "Edit Payment Method" : "Add Payment Method"}
            size="md"
            footer={footer}
        >
            <form onSubmit={handleSubmit} className="space-y-5">
                <FormInput 
                    label="Provider Name (e.g. PhonePe, GPay)" 
                    field="providerName" 
                    value={formData.providerName} 
                    onChange={handleChange} 
                    placeholder="e.g. PhonePe"
                    required 
                />
                <FormInput 
                    label="UPI ID / Merchant ID" 
                    field="upiId" 
                    placeholder="e.g. pinkmoontech@ybl" 
                    value={formData.upiId} 
                    onChange={handleChange} 
                    required 
                />
                <FormInput 
                    label="Display Name" 
                    field="displayName" 
                    value={formData.displayName} 
                    onChange={handleChange} 
                    placeholder="e.g. Primary Business Account"
                    required 
                />

                <div className="flex items-center gap-3 py-2">
                    <input 
                        type="checkbox" 
                        id="isPrimaryDigital"
                        name="isPrimary" 
                        checked={formData.isPrimary} 
                        onChange={handleChange}
                        className="w-4 h-4 rounded border-slate-300 text-brand-blue focus:ring-brand-blue/20"
                    />
                    <label htmlFor="isPrimaryDigital" className="text-[13px] font-semibold text-slate-600 cursor-pointer">
                        Set as Primary Method
                    </label>
                </div>
            </form>
        </Modal>
    );
};
